import type { EventSource } from "./contracts.js";
import type { AgentEvent, RunView } from "./types.js";

/**
 * 実行イベントの購読（FR-4.2 / AC-4）。
 *
 * `EventSource` は独自ヘッダを付けられないので、`fetch` +
 * `ReadableStream` で自前実装する。Bearer トークンが要る API では
 * `EventSource` は使えない。
 *
 * 再開は受信済みの最大 `seq` を `Last-Event-ID` に載せる。
 * サーバは「その seq より後」を返すので、欠落も重複もしない。
 */

export interface SseFrame {
  readonly id: number | null;
  readonly event: string;
  readonly data: string;
}

/**
 * SSE のテキストをフレームに切り分ける（純関数）。
 * チャンク境界がフレームの途中に来ても壊れないよう状態を持つ。
 */
export class SseParser {
  #buffer = "";

  push(chunk: string): SseFrame[] {
    this.#buffer += chunk;
    const frames: SseFrame[] = [];

    for (;;) {
      const boundary = this.#buffer.indexOf("\n\n");
      if (boundary === -1) break;
      const block = this.#buffer.slice(0, boundary);
      this.#buffer = this.#buffer.slice(boundary + 2);

      const frame = parseBlock(block);
      if (frame !== null) frames.push(frame);
    }
    return frames;
  }
}

function parseBlock(block: string): SseFrame | null {
  let id: number | null = null;
  let event = "message";
  const dataLines: string[] = [];

  for (const line of block.split("\n")) {
    // コメント（ハートビート）は無視する。
    if (line.startsWith(":") || line.trim() === "") continue;
    const separator = line.indexOf(":");
    const field = separator === -1 ? line : line.slice(0, separator);
    const value = separator === -1 ? "" : line.slice(separator + 1).replace(/^ /, "");

    if (field === "id") {
      const parsed = Number.parseInt(value, 10);
      if (Number.isFinite(parsed)) id = parsed;
    } else if (field === "event") {
      event = value;
    } else if (field === "data") {
      dataLines.push(value);
    }
  }

  if (dataLines.length === 0) return null;
  return { id, event, data: dataLines.join("\n") };
}

export type ConnectionState = "connecting" | "open" | "closed";

export interface SubscribeOptions {
  readonly onEvent: (event: AgentEvent) => void;
  readonly onError?: (error: unknown) => void;
  readonly onStateChange?: (state: ConnectionState) => void;
  readonly signal?: AbortSignal;
  /**
   * ここより後のイベントだけを受け取る。
   *
   * 履歴を読んだあと続きを追う場合に使う。渡さないと先頭から流れ直し、
   * 既に画面にあるものが二重に届く。
   */
  readonly lastEventId?: number;
  /** 再接続までの待ち時間（ミリ秒）。 */
  readonly retryDelayMs?: number;
  readonly maxRetries?: number;
  readonly sleep?: (ms: number) => Promise<void>;
}

const TERMINAL_EVENTS = new Set(["run.result"]);

/**
 * 実行イベントを購読する。終端イベントを受け取るか abort されるまで続く。
 * 途中で切れたら `Last-Event-ID` を付けて自動再開する。
 */
export async function subscribeToRun(
  client: EventSource,
  runId: string,
  options: SubscribeOptions,
): Promise<void> {
  const sleep =
    options.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const maxRetries = options.maxRetries ?? 5;
  const retryDelayMs = options.retryDelayMs ?? 1000;

  // `signal.aborted` はゲッターで値が変わるが、TS は一度検査すると
  // 以降を narrowing してしまう。関数越しに読んで毎回評価させる。
  const isAborted = (): boolean => options.signal?.aborted === true;

  let lastEventId = options.lastEventId ?? 0;
  let attempts = 0;

  for (;;) {
    if (isAborted()) {
      options.onStateChange?.("closed");
      return;
    }
    options.onStateChange?.("connecting");

    try {
      const response = await client.runs.eventStream(runId, {
        ...(lastEventId > 0 ? { lastEventId } : {}),
        ...(options.signal === undefined ? {} : { signal: options.signal }),
      });
      if (response.body === null) throw new Error("イベントの本文がありません");

      options.onStateChange?.("open");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      const parser = new SseParser();
      let terminated = false;
      // ★ 再試行の予算は「進めたか」で数える。「繋がったか」ではない。
      // 接続成功でリセットすると、サーバが毎回すぐ切る状況で
      // 永久に繋ぎ直し続ける（実際にそうなった）。
      const startedAt = lastEventId;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
          if (frame.id !== null) lastEventId = Math.max(lastEventId, frame.id);
          let payload: AgentEvent;
          try {
            payload = JSON.parse(frame.data) as AgentEvent;
          } catch {
            continue; // 壊れたフレームは飛ばす（購読自体は続ける）
          }
          options.onEvent(payload);
          if (TERMINAL_EVENTS.has(frame.event)) terminated = true;
        }
        if (terminated) break;
      }
      await reader.cancel().catch(() => undefined);

      if (terminated || isAborted()) {
        options.onStateChange?.("closed");
        return;
      }
      // 終端を見ずにストリームが閉じた = 切断。再接続する。
      // 1 件でも新しく受け取れていれば前進しているので、予算を戻す。
      if (lastEventId > startedAt) attempts = 0;
    } catch (error) {
      if (isAborted()) {
        options.onStateChange?.("closed");
        return;
      }
      options.onError?.(error);
    }

    attempts += 1;
    if (attempts > maxRetries) {
      options.onStateChange?.("closed");
      return;
    }
    await sleep(retryDelayMs * attempts);
  }
}

/**
 * 終わった Run のイベントを 1 回の取得で全部読む。
 *
 * ライブ購読は終端イベントを待って再接続まで面倒を見るが、
 * **過去ログを読むだけならそれは有害**。cancelled の Run は
 * `run.result` を持たないので、購読側は「切断された」と解釈して
 * 再接続の待ちに入ってしまう。
 */
export async function fetchRunEvents(client: EventSource, runId: string): Promise<AgentEvent[]> {
  // ★ 履歴の取得は購読ではない。閉じないストリームを読み切ろうとすると
  // 実行中の Run で永久に待つ（「履歴を読み込み中」から進まなくなる）。
  const response = await client.runs.eventStream(runId, { historyOnly: true });
  const text = await response.text();

  const parser = new SseParser();
  const events: AgentEvent[] = [];

  // 末尾に区切りが無い場合があるので足してから流し込む。
  for (const frame of parser.push(text.endsWith("\n\n") ? text : `${text}\n\n`)) {
    try {
      events.push(JSON.parse(frame.data) as AgentEvent);
    } catch {
      // 壊れたフレームは飛ばす。読み込み自体は成功させたい。
    }
  }
  return events;
}

export interface HistoryTurn {
  readonly runId: string;
  readonly prompt: string;
  readonly createdAt: string;
  readonly status: string;
  readonly events: readonly AgentEvent[];
}

const DEFAULT_MAX_RUNS = 20;

/**
 * セッションの過去のやり取りを、古い順のターン列として組み立てる。
 *
 * 1 本の Run のイベント取得に失敗しても、そのターンを空にして続ける
 * （履歴が 1 か所欠けただけで再開できないのは困る）。
 */
export async function loadSessionHistory(
  client: EventSource,
  sessionId: string,
  options: { maxRuns?: number } = {},
): Promise<HistoryTurn[]> {
  const maxRuns = options.maxRuns ?? DEFAULT_MAX_RUNS;
  const page = await client.runs.list({ sessionId, limit: maxRuns });

  // API は新しい順に返す。表示は古い順なので並べ替えてから直近ぶんを取る。
  const ordered = [...page.items].sort((a: RunView, b: RunView) =>
    a.createdAt.localeCompare(b.createdAt),
  );

  return Promise.all(
    ordered.slice(-maxRuns).map(async (run) => ({
      runId: run.id,
      prompt: run.prompt,
      createdAt: run.createdAt,
      status: run.status,
      events: await fetchRunEvents(client, run.id).catch(() => []),
    })),
  );
}
